import { Token, tokenDTO } from './data-model.token';
import { editStateEnumType, spellingStateEnum } from './data-model.enums';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

import {
  createFindApostropheQuotesRe,
  createSplitOnWhitespaceRe,
  createInvalidTokenCharRe,
  createHyphenRe,
  // TODO: the follwoing should probably (possibly) be turned into factrory functions in data-model.regexp.ts
  TRAILING_PUNCTUATION,
  ALPHA,
  ABBREVIATION_RE,
  SPECIAL_PATTERNS,
  CLAUSE_DELIMITERS,
} from './data-model.regexp';

const logger: CapicheLoggerCore = new CapicheLoggerCore('tokens:', false);

function maybeSplitHyphenated(segment: string): string[] {
  const pureAlphaRe = new RegExp(`^[${ALPHA}]+$`); // regex to test for purely alphabetic strings
  const hyphenRe = createHyphenRe(); // regex to find hyphens
  // Fast exit if there are no hyphens/dashes at all
  if (!hyphenRe.test(segment)) return [segment];

  // Split on any hyphen/dash variant
  const parts: string[] = segment.split(hyphenRe);

  // Split only if *all* parts are alphabetic and longer than two chars
  const allAlphabeticAndLong = parts.every((p) => pureAlphaRe.test(p) && p.length > 2);

  return allAlphabeticAndLong ? parts : [segment];
}

export class Tokens {
  constructor(public readonly tokens: Token[]) {
    logger.log('constructor:', tokens);
  }

  // Factory methods
  /**
   * Tokenisation rules for Capiche
   * --------------------------------
   * Split only on meaningful boundaries and preserve the user’s original text.
   *
   * 1️⃣ Primary splitting
   *    - Split on any whitespace (\s+)
   *    - Within each whitespace segment, split further on non-letter symbols
   *      unless the symbol belongs to a recognised construct (email, URL, etc.)
   *
   * 2️⃣ Ignored constructs (kept whole but marked `ignore` later)
   *    - Emails: me@example.com
   *    - URLs: https://capiche.ai
   *    - Hashtags: #dyslexia
   *    - Mentions: @paul
   *    - IP addresses: 192.168.0.1
   *    - Numeric or alphanumeric codes: v2.0.1, NZ$5, A12B
   *
   * 3️⃣ Internal punctuation handling
   *    - Apostrophes (and variants) are kept within words: don’t, it’s, John’s
   *    - Hyphens are preserved inside tokens for now: long-term, well-being
   *    - Underscores and slashes split words: under_score → under, score
   *    - Periods are only kept inside recognised constructs (URLs, IPs, etc.)
   *    - All other symbols, emoji, and punctuation are treated as boundaries
   *
   * 4️⃣ Normalisation
   *    - Store tokens exactly as typed
   *    - Perform normalisation (case-folding, diacritic removal, punctuation
   *      stripping) only inside dictionary lookup, not here
   *
   * 5️⃣ Summary
   *    Split on whitespace → keep meaningful constructs → preserve apostrophes
   *    and hyphens → discard other punctuation → normalise later in lookup.
   */

  static fromString(input: string): Tokens {
    const t0 = performance.now();
    const tokens: Token[] = [];

    const trailingRe = new RegExp(`[${TRAILING_PUNCTUATION}]+$`); // create regex for trimming trailing punctuation
    const invalidTokenRe = createInvalidTokenCharRe(); // create regex for splitting on invalid token characters

    const preprocessed = input
      .replace(createFindApostropheQuotesRe(), ' ') // remove unwanted apostrophes/quotes
      .replace(trailingRe, ''); // remove any trailing punctuation form the input

    // for each whitespace-separated segment
    for (const match of preprocessed.matchAll(createSplitOnWhitespaceRe())) {
      let segment = match[0];
      const offset = match.index;

      // --- abbreviations ---
      if (ABBREVIATION_RE.test(segment)) {
        // strip comma/semicolon after abbreviation but keep period inside pattern
        segment = segment.replace(new RegExp(`(?<=\\.)[${CLAUSE_DELIMITERS}]+$`), '');
        tokens.push(Token.fromString(segment, offset, spellingStateEnum.ignore)); // mark abbreviations as 'ignore' for spelling highlighting
        continue;
      }

      // trim trailing punctuation
      segment = segment.replace(trailingRe, '');

      // if this matches a special token just store it as-is
      if (SPECIAL_PATTERNS.some((re) => re.test(segment))) {
        tokens.push(Token.fromString(segment, offset, spellingStateEnum.ignore)); // mark special patterns as 'ignore' for spelling highlighting
        continue;
      }

      const parts: string[] = segment.split(invalidTokenRe).filter(Boolean).flatMap(maybeSplitHyphenated); // split segment by invalid and then by hyphens

      let localPos = 0;
      for (const p of parts) {
        const localIndex = segment.indexOf(p, localPos);
        const absIndex = offset + localIndex;
        tokens.push(Token.fromString(p, absIndex));
        localPos = localIndex + p.length;
      }
    }

    const t: string = (performance.now() - t0).toFixed(2);
    logger.log(`fromString: tokenized "${input}" into ${tokens.length} tokens in ${t} ms`);
    return new Tokens(tokens);
  }

  static fromDTOs(dtos: tokenDTO[]): Tokens {
    return new Tokens(dtos.map((dto) => Token.fromDTO(dto)));
  }

  // Serialization
  toDTOs(): tokenDTO[] {
    return this.tokens.map((token) => token.toDTO());
  }

  get(index: number): Token | undefined {
    // Safely return token or undefined if out of range
    return this.tokens[index];
  }

  /**
   * Returns a slice of tokens starting at index i and including delta tokens.
   * If delta is negative, returns tokens[i ... i+delta)
   * If delta is zero, returns tokens[i]
   * If delta is positive, returns tokens[i+delta ... i)
   * Always clamps indexes safely within bounds.
   */
  getRange(i: number, delta: number): Token[] {
    const n = this.tokens.length;
    if (n === 0) return [];

    // Always include the current token
    if (delta === 0) return this.tokens.slice(i, i + 1);

    let start: number;
    let end: number;

    if (delta > 0) {
      // input has extra tokens → look ahead in GPT
      start = i;
      end = Math.min(n, i + delta + 1);
    } else {
      // GPT has extra tokens → look back
      start = Math.max(0, i + delta);
      end = i + 1;
    }

    // clamp
    start = Math.max(0, Math.min(start, n));
    end = Math.max(start + 1, Math.min(end, n));

    return this.tokens.slice(start, end);
  }

  // Collection utilities
  filter(predicate: (token: Token) => boolean): Tokens {
    return new Tokens(this.tokens.filter(predicate));
  }

  map(fn: (token: Token) => Token): Tokens {
    return new Tokens(this.tokens.map(fn));
  }

  // Business logic utilities

  filterOnEditState(retainStates: editStateEnumType[]): Tokens {
    return this.filter((t: Token) => retainStates.includes(t.dto.editState));
  }

  getMisspelledTokens(): Tokens {
    return this.filter((token) => token.isMisspelled());
  }

  getTokensWithSuggestions(): Tokens {
    return this.filter((token) => token.hasSuggestions());
  }

  getTokenCount(): number {
    return this.tokens.length;
  }

  getMisspelledTokenCount(): number {
    return this.getMisspelledTokens().tokens.length;
  }
}
